/**
 * `<ShortcutsCheatsheet />` — Story 5.4 (UX §4.1 line 890, AC line 1017).
 *
 * Modal listing keyboard shortcuts. Consumed by Story 7.5 admin
 * moderation queue, Story 10.4 reveal control, etc.
 *
 * Ownership boundaries (Critical Scope Decision #9):
 *   - Component owns `Esc` → `onClose` (closing keybinding is
 *     behaviorally tied to "while open").
 *   - Component does NOT own the `?` keybinding that opens the modal.
 *     That's per-surface (Story 7.5 admin queue, Story 10.4 reveal
 *     control, etc., each own their own `?` listener and decide
 *     whether the cheatsheet is even available).
 *
 * Behavior:
 *   - Closed (`isOpen=false`) renders nothing.
 *   - Open: renders backdrop + modal panel.
 *   - `Esc` keydown calls `onClose`.
 *   - Backdrop click calls `onClose`.
 *   - Tab / Shift+Tab wraps focus inside the modal (uses
 *     `__lib/focus-trap.ts` per Critical Scope Decision #10).
 *   - On open, focus moves to the first focusable inside the modal.
 *   - On close, focus restores to the element that triggered open.
 *
 * Aria:
 *   - `role="dialog"` + `aria-modal="true"` + `aria-labelledby`
 *     linking to the heading id.
 */
import { useEffect, useId, useRef } from 'react';
import { getFocusable, wrapTabFocus } from './__lib/focus-trap';

export interface ShortcutEntry {
  keys: string[];
  description: string;
}

export interface ShortcutsCheatsheetProps {
  isOpen: boolean;
  onClose: () => void;
  shortcuts: ShortcutEntry[];
  title?: string;
}

const DEFAULT_TITLE = 'Keyboard shortcuts';

/**
 * `ShortcutsCheatsheet` — modal with keyboard shortcut listing.
 */
export function ShortcutsCheatsheet({
  isOpen,
  onClose,
  shortcuts,
  title = DEFAULT_TITLE,
}: ShortcutsCheatsheetProps) {
  const headingId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const dialog = dialogRef.current;
    if (dialog) {
      const focusable = getFocusable(dialog);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        dialog.focus();
      }
    }

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialog) {
        const focusable = getFocusable(dialog);
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        wrapTabFocus(e, focusable[0], focusable[focusable.length - 1]);
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      // Restore focus on close.
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Click on backdrop (not the modal panel) closes.
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      data-testid="shortcuts-backdrop"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        data-testid="shortcuts-dialog"
        style={{
          background: 'var(--bg-surface-raised)',
          color: 'var(--text-primary)',
          padding: 'var(--space-5)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--elev-3)',
          minWidth: 320,
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          fontFamily: 'var(--font-sans)',
        }}
      >
        <h2
          id={headingId}
          style={{
            margin: 0,
            marginBottom: 'var(--space-4)',
            fontSize: 'var(--text-xl-size)',
          }}
        >
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close shortcuts"
          data-testid="shortcuts-close"
          style={{
            position: 'absolute',
            top: 'var(--space-3)',
            right: 'var(--space-3)',
            background: 'transparent',
            color: 'var(--text-primary)',
            border: 'none',
            fontSize: 'var(--text-lg-size)',
            cursor: 'pointer',
            outlineWidth: 2,
            outlineStyle: 'solid',
            outlineColor: 'var(--brand-primary)',
            outlineOffset: 2,
          }}
        >
          ×
        </button>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 'var(--space-2) var(--space-4)',
            margin: 0,
          }}
        >
          {shortcuts.map((shortcut, idx) => (
            <ShortcutRow key={idx} shortcut={shortcut} />
          ))}
        </dl>
      </div>
    </div>
  );
}

function ShortcutRow({ shortcut }: { shortcut: ShortcutEntry }) {
  return (
    <>
      <dt style={{ display: 'flex', gap: 'var(--space-1)' }}>
        {shortcut.keys.map((k, i) => (
          <kbd
            key={i}
            style={{
              padding: '2px var(--space-2)',
              background: 'var(--bg-surface-sunken)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-xs-size)',
            }}
          >
            {k}
          </kbd>
        ))}
      </dt>
      <dd style={{ margin: 0, color: 'var(--text-secondary)' }}>
        {shortcut.description}
      </dd>
    </>
  );
}
